import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { 
  CalendarDays, 
  Lock, 
  Unlock, 
  List, 
  ArrowLeft,
  Loader2,
  Trash2,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  Banknote,
  FileCheck,
  LogOut,
  User,
  Plus,
  MessageSquare,
  HelpCircle,
  Edit,
  Star,
  Database
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('calendar');
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(location.state?.user || null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [manuallyBlockedDates, setManuallyBlockedDates] = useState([]);
  const [bookedDates, setBookedDates] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  
  // Manual booking form state
  const [manualBooking, setManualBooking] = useState({
    date: null,
    venueName: '',
    venueAddress: '',
    eventType: '',
    contactName: '',
    phone: '',
    email: '',
    arrivalTime: '',
    setupTime: '',
    performanceTime: '',
    showFormat: '',
    fee: '',
    deposit: '',
    notes: '',
    status: 'confirmed',
    depositReceived: false,
    contractSigned: true
  });

  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [newTestimonial, setNewTestimonial] = useState({
    text: '',
    author: '',
    role: '',
    rating: 5,
    image: '',
    is_logo: false
  });

  // FAQs state
  const [faqs, setFaqs] = useState([]);
  const [editingFaq, setEditingFaq] = useState(null);
  const [newFaq, setNewFaq] = useState({
    question: '',
    answer: ''
  });

  // Check authentication on mount
  useEffect(() => {
    // If user was passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setAuthLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Not authenticated');
        }
        
        const userData = await response.json();
        setUser(userData);
        setAuthLoading(false);
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/admin/login', { replace: true });
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/admin/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    if (!authLoading && user) {
      fetchBlockedDates();
      fetchBookings();
      fetchTestimonials();
      fetchFaqs();
    }
  }, [authLoading, user]);

  const fetchBlockedDates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/blocked-dates`);
      const data = await response.json();
      // Parse dates correctly to avoid timezone issues
      const parseDateStr = (dateStr) => {
        const [year, month, day] = dateStr.split('-');
        return new Date(year, month - 1, day);
      };
      
      const dates = data.blocked_dates.map(parseDateStr);
      const manualDates = (data.manually_blocked || []).map(parseDateStr);
      const bookingDates = (data.booked_dates || []).map(parseDateStr);
      
      setBlockedDates(dates);
      setManuallyBlockedDates(manualDates);
      setBookedDates(bookingDates);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bookings`);
      const data = await response.json();
      setBookings(data.bookings || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await fetch(`${API_URL}/api/testimonials`);
      const data = await response.json();
      setTestimonials(data.testimonials || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const fetchFaqs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/faqs`);
      const data = await response.json();
      setFaqs(data.faqs || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  // Testimonial CRUD handlers
  const handleCreateTestimonial = async () => {
    if (!newTestimonial.text || !newTestimonial.author) {
      toast({ title: "Error", description: "Text and Author are required", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/testimonials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newTestimonial),
      });
      if (!response.ok) throw new Error('Failed to create testimonial');
      toast({ title: "Success", description: "Testimonial added" });
      setNewTestimonial({ text: '', author: '', role: '', rating: 5, image: '', is_logo: false });
      fetchTestimonials();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTestimonial = async () => {
    if (!editingTestimonial) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/testimonials/${editingTestimonial.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editingTestimonial),
      });
      if (!response.ok) throw new Error('Failed to update testimonial');
      toast({ title: "Success", description: "Testimonial updated" });
      setEditingTestimonial(null);
      fetchTestimonials();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/testimonials/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to delete testimonial');
      toast({ title: "Success", description: "Testimonial deleted" });
      fetchTestimonials();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // FAQ CRUD handlers
  const handleCreateFaq = async () => {
    if (!newFaq.question || !newFaq.answer) {
      toast({ title: "Error", description: "Question and Answer are required", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/faqs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newFaq),
      });
      if (!response.ok) throw new Error('Failed to create FAQ');
      toast({ title: "Success", description: "FAQ added" });
      setNewFaq({ question: '', answer: '' });
      fetchFaqs();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFaq = async () => {
    if (!editingFaq) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/faqs/${editingFaq.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editingFaq),
      });
      if (!response.ok) throw new Error('Failed to update FAQ');
      toast({ title: "Success", description: "FAQ updated" });
      setEditingFaq(null);
      fetchFaqs();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFaq = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/faqs/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to delete FAQ');
      toast({ title: "Success", description: "FAQ deleted" });
      fetchFaqs();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Seed data handlers
  const handleSeedTestimonials = async () => {
    if (!window.confirm('Seed initial testimonials? This will only work if no testimonials exist yet.')) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/seed/testimonials`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      toast({ title: data.success ? "Success" : "Info", description: data.message });
      if (data.success) fetchTestimonials();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSeedFaqs = async () => {
    if (!window.confirm('Seed initial FAQs? This will only work if no FAQs exist yet.')) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/seed/faqs`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      toast({ title: data.success ? "Success" : "Info", description: data.message });
      if (data.success) fetchFaqs();
    } catch (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleBlockDate = async () => {
    if (!selectedDate) {
      toast({
        title: "No Date Selected",
        description: "Please select a date to block.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const dateStr = formatDateForApi(selectedDate);
      const response = await fetch(`${API_URL}/api/blocked-dates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: dateStr,
          reason: blockReason || 'manually_blocked'
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to block date');
      }

      toast({
        title: "Date Blocked",
        description: `${selectedDate.toLocaleDateString('en-GB')} has been blocked.`,
      });
      
      setSelectedDate(null);
      setBlockReason('');
      fetchBlockedDates();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockDate = async (dateStr) => {
    console.log('Attempting to unblock date:', dateStr);
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/blocked-dates/${dateStr}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      console.log('Unblock response status:', response.status);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to unblock date');
      }

      toast({
        title: "Date Unblocked",
        description: `${new Date(dateStr + 'T12:00:00').toLocaleDateString('en-GB')} is now available.`,
      });
      
      setSelectedDate(null);
      await fetchBlockedDates();
    } catch (error) {
      console.error('Unblock error:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/cancel`, {
        method: 'POST',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel booking');
      }

      toast({
        title: "Booking Cancelled",
        description: "The booking has been cancelled successfully.",
      });
      
      fetchBookings();
      fetchBlockedDates();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeposit = async (bookingId, deposit) => {
    if (!window.confirm(`Confirm deposit of £${deposit} has been received?`)) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/confirm-deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: bookingId,
          received_date: new Date().toISOString().split('T')[0]
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to confirm deposit');
      }

      toast({
        title: "Deposit Confirmed",
        description: "The deposit has been marked as received.",
      });
      
      fetchBookings();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManualBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!manualBooking.date || !manualBooking.venueName || !manualBooking.contactName || !manualBooking.fee) {
      toast({
        title: "Missing Fields",
        description: "Please fill in Date, Venue Name, Contact Name, and Fee.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const dateStr = formatDateForApi(manualBooking.date);
      const fee = parseFloat(manualBooking.fee);
      const deposit = manualBooking.deposit ? parseFloat(manualBooking.deposit) : fee * 0.2;
      
      const response = await fetch(`${API_URL}/api/admin/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: dateStr,
          venue_name: manualBooking.venueName,
          venue_address: manualBooking.venueAddress,
          event_type: manualBooking.eventType || 'private',
          contact_name: manualBooking.contactName,
          phone: manualBooking.phone,
          email: manualBooking.email,
          arrival_time: manualBooking.arrivalTime,
          setup_time: manualBooking.setupTime,
          performance_time: manualBooking.performanceTime,
          show_format: manualBooking.showFormat || '2x45',
          fee: fee,
          deposit: deposit,
          notes: manualBooking.notes,
          status: manualBooking.status,
          deposit_received: manualBooking.depositReceived,
          contract_signed: manualBooking.contractSigned
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create booking');
      }

      toast({
        title: "Booking Added",
        description: `Booking for ${manualBooking.venueName} on ${dateStr} has been added.`,
      });

      // Reset form
      setManualBooking({
        date: null,
        venueName: '',
        venueAddress: '',
        eventType: '',
        contactName: '',
        phone: '',
        email: '',
        arrivalTime: '',
        setupTime: '',
        performanceTime: '',
        showFormat: '',
        fee: '',
        deposit: '',
        notes: '',
        status: 'confirmed',
        depositReceived: false,
        contractSigned: true
      });

      fetchBookings();
      fetchBlockedDates();
      setActiveTab('bookings');
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const isDateBlocked = (date) => {
    return blockedDates.some(
      (blockedDate) => blockedDate.toDateString() === date.toDateString()
    );
  };

  const isDateManuallyBlocked = (date) => {
    return manuallyBlockedDates.some(
      (blockedDate) => blockedDate.toDateString() === date.toDateString()
    );
  };

  const isDateBookedByClient = (date) => {
    return bookedDates.some(
      (bookedDate) => bookedDate.toDateString() === date.toDateString()
    );
  };

  // Helper to format date as YYYY-MM-DD for API calls
  const formatDateForApi = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-400';
      case 'deposit_paid': return 'text-cyan-400';
      case 'pending': return 'text-yellow-400';
      case 'cancelled': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <CheckCircle size={16} />;
      case 'deposit_paid': return <Banknote size={16} />;
      case 'pending': return <Clock size={16} />;
      case 'cancelled': return <XCircle size={16} />;
      default: return null;
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-gold animate-spin mx-auto mb-4" />
          <p className="text-gold">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-black/95 border-b border-gold/30 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gold hover:text-gold/80 transition-colors">
              <ArrowLeft size={24} />
            </Link>
            <h1 className="text-2xl font-bold text-gold">Admin Panel</h1>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <div className="flex items-center gap-3">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-8 h-8 rounded-full border border-gold/30"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                    <User size={16} className="text-gold" />
                  </div>
                )}
                <span className="text-silver text-sm hidden sm:block">{user.name}</span>
              </div>
            )}
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-silver hover:text-gold hover:bg-gold/10"
              data-testid="admin-logout-btn"
            >
              <LogOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-black/80 border-b border-gold/20 px-6">
        <div className="max-w-7xl mx-auto flex gap-2">
          <button
            onClick={() => setActiveTab('calendar')}
            className={`px-6 py-4 font-semibold transition-all flex items-center gap-2 border-b-2 ${
              activeTab === 'calendar'
                ? 'border-gold text-gold'
                : 'border-transparent text-silver hover:text-gold/80'
            }`}
            data-testid="admin-tab-calendar"
          >
            <CalendarDays size={20} />
            Manage Dates
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-6 py-4 font-semibold transition-all flex items-center gap-2 border-b-2 ${
              activeTab === 'bookings'
                ? 'border-gold text-gold'
                : 'border-transparent text-silver hover:text-gold/80'
            }`}
            data-testid="admin-tab-bookings"
          >
            <List size={20} />
            All Bookings
          </button>
          <button
            onClick={() => setActiveTab('add-booking')}
            className={`px-6 py-4 font-semibold transition-all flex items-center gap-2 border-b-2 ${
              activeTab === 'add-booking'
                ? 'border-gold text-gold'
                : 'border-transparent text-silver hover:text-gold/80'
            }`}
            data-testid="admin-tab-add-booking"
          >
            <Plus size={20} />
            Add Booking
          </button>
          <button
            onClick={() => setActiveTab('testimonials')}
            className={`px-6 py-4 font-semibold transition-all flex items-center gap-2 border-b-2 ${
              activeTab === 'testimonials'
                ? 'border-gold text-gold'
                : 'border-transparent text-silver hover:text-gold/80'
            }`}
            data-testid="admin-tab-testimonials"
          >
            <MessageSquare size={20} />
            Testimonials
          </button>
          <button
            onClick={() => setActiveTab('faqs')}
            className={`px-6 py-4 font-semibold transition-all flex items-center gap-2 border-b-2 ${
              activeTab === 'faqs'
                ? 'border-gold text-gold'
                : 'border-transparent text-silver hover:text-gold/80'
            }`}
            data-testid="admin-tab-faqs"
          >
            <HelpCircle size={20} />
            FAQs
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Calendar Section */}
            <div className="bg-black/50 border border-gold/30 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-gold mb-4 flex items-center gap-2">
                <CalendarDays size={24} />
                Select Date to Block/Unblock
              </h2>
              <p className="text-silver text-sm mb-6">
                Red dates are already blocked. Click any date to select it.
              </p>
              
              <div className="flex justify-center mb-6">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  modifiers={{
                    blocked: blockedDates
                  }}
                  modifiersStyles={{
                    blocked: { 
                      backgroundColor: 'rgba(239, 68, 68, 0.3)',
                      color: '#f87171',
                      fontWeight: 'bold'
                    }
                  }}
                  className="rounded-lg border-2 border-gold/30 bg-black/50 p-4"
                  data-testid="admin-calendar"
                />
              </div>

              {selectedDate && (
                <div className="space-y-4 border-t border-gold/20 pt-4">
                  <p className="text-gold font-semibold">
                    Selected: {selectedDate.toLocaleDateString('en-GB', { 
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                    })}
                  </p>
                  
                  {isDateBlocked(selectedDate) ? (
                    <Button
                      onClick={() => handleUnblockDate(formatDateForApi(selectedDate))}
                      disabled={loading}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      data-testid="unblock-date-btn"
                    >
                      {loading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Unlock className="mr-2" size={18} />
                      )}
                      Unblock This Date
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <Input
                        placeholder="Reason (optional) e.g., Personal, Holiday"
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        className="bg-black/50 border-gold/30 text-white"
                        data-testid="block-reason-input"
                      />
                      <Button
                        onClick={handleBlockDate}
                        disabled={loading}
                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                        data-testid="block-date-btn"
                      >
                        {loading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Lock className="mr-2" size={18} />
                        )}
                        Block This Date
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Blocked Dates List */}
            <div className="bg-black/50 border border-gold/30 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-gold mb-4 flex items-center gap-2">
                <Lock size={24} />
                Currently Blocked Dates
              </h2>
              
              {blockedDates.length === 0 ? (
                <p className="text-silver text-center py-8">
                  No dates are currently blocked.
                </p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                  {blockedDates
                    .sort((a, b) => a - b)
                    .map((date, index) => {
                      const isManual = isDateManuallyBlocked(date);
                      const isBooked = isDateBookedByClient(date);
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-black/30 border border-gold/20 rounded-lg"
                          data-testid={`blocked-date-${formatDateForApi(date)}`}
                        >
                          <div className="flex flex-col">
                            <span className="text-white">
                              {date.toLocaleDateString('en-GB', { 
                                weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' 
                              })}
                            </span>
                            <span className={`text-xs ${isBooked ? 'text-cyan-400' : 'text-yellow-400'}`}>
                              {isBooked ? '📅 Client Booking' : '🔒 Manually Blocked'}
                            </span>
                          </div>
                          {isManual ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleUnblockDate(formatDateForApi(date));
                              }}
                              disabled={loading}
                              className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                              data-testid={`unblock-btn-${formatDateForApi(date)}`}
                            >
                              {loading ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                            </Button>
                          ) : (
                            <span className="text-xs text-silver px-2">
                              Cancel booking to free
                            </span>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="bg-black/50 border border-gold/30 rounded-2xl p-6">
            <h2 className="text-xl font-bold text-gold mb-6 flex items-center gap-2">
              <List size={24} />
              All Bookings ({bookings.length})
            </h2>

            {bookings.length === 0 ? (
              <p className="text-silver text-center py-8">
                No bookings yet.
              </p>
            ) : (
              <div className="space-y-4">
                {bookings
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map((booking) => (
                    <div
                      key={booking.id}
                      className="p-4 bg-black/30 border border-gold/20 rounded-xl"
                      data-testid={`booking-${booking.id}`}
                    >
                      <div className="flex flex-wrap justify-between items-start gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <span className={`flex items-center gap-1 font-semibold ${getStatusColor(booking.status)}`}>
                              {getStatusIcon(booking.status)}
                              {booking.status.replace('_', ' ').toUpperCase()}
                            </span>
                            {booking.contract_signed && (
                              <span className="text-xs bg-green-900/30 text-green-400 px-2 py-1 rounded">
                                Contract Signed
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-bold text-white">
                            {booking.venue_name}
                          </h3>
                          <p className="text-silver text-sm">{booking.venue_address}</p>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-gold font-bold text-lg">
                            £{parseFloat(booking.fee).toFixed(2)}
                          </p>
                          <p className="text-silver text-sm">
                            {new Date(booking.date).toLocaleDateString('en-GB', {
                              weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gold/10">
                        <div>
                          <p className="text-gold/70 text-xs uppercase">Contact</p>
                          <p className="text-white text-sm">{booking.contact_name}</p>
                          <p className="text-silver text-xs">{booking.email}</p>
                          <p className="text-silver text-xs">{booking.phone}</p>
                        </div>
                        <div>
                          <p className="text-gold/70 text-xs uppercase">Event Type</p>
                          <p className="text-white text-sm capitalize">{booking.event_type}</p>
                          <p className="text-silver text-xs">Format: {booking.show_format}</p>
                        </div>
                        <div>
                          <p className="text-gold/70 text-xs uppercase">Times</p>
                          <p className="text-silver text-xs">Arrival: {booking.arrival_time}</p>
                          <p className="text-silver text-xs">Setup: {booking.setup_time}</p>
                          <p className="text-silver text-xs">Performance: {booking.performance_time}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap justify-between items-center gap-3 mt-4 pt-4 border-t border-gold/10">
                        <div className="text-xs text-silver">
                          <span>Deposit: £{parseFloat(booking.deposit).toFixed(2)}</span>
                          <span className="mx-2">|</span>
                          <span>Balance: £{parseFloat(booking.balance).toFixed(2)}</span>
                          {booking.deposit_received_date && (
                            <>
                              <span className="mx-2">|</span>
                              <span className="text-green-400">
                                Deposit received: {new Date(booking.deposit_received_date).toLocaleDateString('en-GB')}
                              </span>
                            </>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          {booking.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleConfirmDeposit(booking.id, booking.deposit)}
                              className="border-green-500/50 text-green-400 hover:bg-green-900/20"
                              data-testid={`confirm-deposit-${booking.id}`}
                            >
                              <FileCheck size={14} className="mr-1" />
                              Confirm Deposit
                            </Button>
                          )}
                          {booking.status !== 'cancelled' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCancelBooking(booking.id)}
                              className="border-red-500/50 text-red-400 hover:bg-red-900/20"
                              data-testid={`cancel-booking-${booking.id}`}
                            >
                              <XCircle size={14} className="mr-1" />
                              Cancel
                            </Button>
                          )}
                        </div>
                      </div>

                      {booking.notes && (
                        <div className="mt-3 pt-3 border-t border-gold/10">
                          <p className="text-gold/70 text-xs uppercase mb-1">Notes</p>
                          <p className="text-silver text-sm">{booking.notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Add Booking Tab */}
        {activeTab === 'add-booking' && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-black/50 border border-gold/30 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-gold mb-6 flex items-center gap-2">
                <Plus size={24} />
                Add Manual Booking
              </h2>
              <p className="text-silver text-sm mb-6">
                Use this form to add bookings taken outside the online system or pre-existing contracted bookings.
              </p>

              <form onSubmit={handleManualBookingSubmit} className="space-y-6">
                {/* Date Selection */}
                <div>
                  <label className="block text-gold mb-2 font-semibold">Event Date *</label>
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={manualBooking.date}
                      onSelect={(date) => setManualBooking({...manualBooking, date})}
                      disabled={(date) => date < new Date()}
                      modifiers={{
                        blocked: blockedDates
                      }}
                      modifiersStyles={{
                        blocked: { 
                          backgroundColor: 'rgba(239, 68, 68, 0.3)',
                          color: '#f87171',
                          fontWeight: 'bold'
                        }
                      }}
                      className="rounded-lg border-2 border-gold/30 bg-black/50 p-4"
                    />
                  </div>
                  {manualBooking.date && (
                    <p className="text-center text-gold mt-2">
                      Selected: {manualBooking.date.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  )}
                </div>

                {/* Venue Details */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gold mb-2 font-semibold">Venue Name *</label>
                    <Input
                      value={manualBooking.venueName}
                      onChange={(e) => setManualBooking({...manualBooking, venueName: e.target.value})}
                      placeholder="e.g., Grand Hotel Ballroom"
                      className="bg-black/50 border-gold/30 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gold mb-2 font-semibold">Event Type</label>
                    <Select
                      value={manualBooking.eventType}
                      onValueChange={(value) => setManualBooking({...manualBooking, eventType: value})}
                    >
                      <SelectTrigger className="bg-black/50 border-gold/30 text-white">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-black border-gold/30">
                        <SelectItem value="wedding">Wedding</SelectItem>
                        <SelectItem value="corporate">Corporate</SelectItem>
                        <SelectItem value="private">Private Party</SelectItem>
                        <SelectItem value="festival">Festival</SelectItem>
                        <SelectItem value="theatre">Theatre</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-gold mb-2 font-semibold">Venue Address</label>
                  <Textarea
                    value={manualBooking.venueAddress}
                    onChange={(e) => setManualBooking({...manualBooking, venueAddress: e.target.value})}
                    placeholder="Full venue address"
                    className="bg-black/50 border-gold/30 text-white"
                    rows={2}
                  />
                </div>

                {/* Client Details */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gold mb-2 font-semibold">Contact Name *</label>
                    <Input
                      value={manualBooking.contactName}
                      onChange={(e) => setManualBooking({...manualBooking, contactName: e.target.value})}
                      placeholder="Client name"
                      className="bg-black/50 border-gold/30 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gold mb-2 font-semibold">Phone</label>
                    <Input
                      type="tel"
                      value={manualBooking.phone}
                      onChange={(e) => setManualBooking({...manualBooking, phone: e.target.value})}
                      placeholder="07XXX XXXXXX"
                      className="bg-black/50 border-gold/30 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gold mb-2 font-semibold">Email</label>
                    <Input
                      type="email"
                      value={manualBooking.email}
                      onChange={(e) => setManualBooking({...manualBooking, email: e.target.value})}
                      placeholder="client@email.com"
                      className="bg-black/50 border-gold/30 text-white"
                    />
                  </div>
                </div>

                {/* Times */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gold mb-2 font-semibold">Arrival Time</label>
                    <Input
                      type="time"
                      value={manualBooking.arrivalTime}
                      onChange={(e) => setManualBooking({...manualBooking, arrivalTime: e.target.value})}
                      className="bg-black/50 border-gold/30 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gold mb-2 font-semibold">Setup Time</label>
                    <Input
                      type="time"
                      value={manualBooking.setupTime}
                      onChange={(e) => setManualBooking({...manualBooking, setupTime: e.target.value})}
                      className="bg-black/50 border-gold/30 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gold mb-2 font-semibold">Performance Time</label>
                    <Input
                      type="time"
                      value={manualBooking.performanceTime}
                      onChange={(e) => setManualBooking({...manualBooking, performanceTime: e.target.value})}
                      className="bg-black/50 border-gold/30 text-white"
                    />
                  </div>
                </div>

                {/* Show & Payment */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gold mb-2 font-semibold">Show Format</label>
                    <Select
                      value={manualBooking.showFormat}
                      onValueChange={(value) => setManualBooking({...manualBooking, showFormat: value})}
                    >
                      <SelectTrigger className="bg-black/50 border-gold/30 text-white">
                        <SelectValue placeholder="Select format" />
                      </SelectTrigger>
                      <SelectContent className="bg-black border-gold/30">
                        <SelectItem value="1x60">1 x 60 minutes</SelectItem>
                        <SelectItem value="2x45">2 x 45 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-gold mb-2 font-semibold">Total Fee (£) *</label>
                    <Input
                      type="number"
                      value={manualBooking.fee}
                      onChange={(e) => setManualBooking({...manualBooking, fee: e.target.value})}
                      placeholder="1500"
                      min="0"
                      step="50"
                      className="bg-black/50 border-gold/30 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gold mb-2 font-semibold">Deposit (£)</label>
                    <Input
                      type="number"
                      value={manualBooking.deposit}
                      onChange={(e) => setManualBooking({...manualBooking, deposit: e.target.value})}
                      placeholder="Auto: 20% of fee"
                      min="0"
                      step="50"
                      className="bg-black/50 border-gold/30 text-white"
                    />
                  </div>
                </div>

                {/* Status & Checkboxes */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gold mb-2 font-semibold">Status</label>
                    <Select
                      value={manualBooking.status}
                      onValueChange={(value) => setManualBooking({...manualBooking, status: value})}
                    >
                      <SelectTrigger className="bg-black/50 border-gold/30 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-black border-gold/30">
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-white cursor-pointer">
                      <input
                        type="checkbox"
                        checked={manualBooking.depositReceived}
                        onChange={(e) => setManualBooking({...manualBooking, depositReceived: e.target.checked})}
                        className="w-5 h-5 accent-gold"
                      />
                      Deposit Received
                    </label>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-white cursor-pointer">
                      <input
                        type="checkbox"
                        checked={manualBooking.contractSigned}
                        onChange={(e) => setManualBooking({...manualBooking, contractSigned: e.target.checked})}
                        className="w-5 h-5 accent-gold"
                      />
                      Contract Signed
                    </label>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-gold mb-2 font-semibold">Notes</label>
                  <Textarea
                    value={manualBooking.notes}
                    onChange={(e) => setManualBooking({...manualBooking, notes: e.target.value})}
                    placeholder="Any special requirements or notes..."
                    className="bg-black/50 border-gold/30 text-white"
                    rows={3}
                  />
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold text-black hover:bg-gold/90 font-semibold py-6"
                  data-testid="submit-manual-booking"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding Booking...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2" size={18} />
                      Add Booking
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <div className="space-y-6">
            {/* Seed Button */}
            {testimonials.length === 0 && (
              <div className="bg-black/50 border border-gold/30 rounded-2xl p-6 text-center">
                <Database className="h-12 w-12 text-gold mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gold mb-2">No Testimonials Found</h3>
                <p className="text-silver mb-4">Seed the database with initial testimonials from the website.</p>
                <Button
                  onClick={handleSeedTestimonials}
                  disabled={loading}
                  className="bg-gold text-black hover:bg-gold/90"
                  data-testid="seed-testimonials-btn"
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2" size={18} />}
                  Seed Testimonials
                </Button>
              </div>
            )}

            {/* Add New Testimonial */}
            <div className="bg-black/50 border border-gold/30 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-gold mb-4 flex items-center gap-2">
                <Plus size={24} />
                Add New Testimonial
              </h2>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gold mb-2 text-sm">Author Name *</label>
                  <Input
                    value={newTestimonial.author}
                    onChange={(e) => setNewTestimonial({...newTestimonial, author: e.target.value})}
                    placeholder="e.g., John Smith"
                    className="bg-black/50 border-gold/30 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gold mb-2 text-sm">Role/Title</label>
                  <Input
                    value={newTestimonial.role}
                    onChange={(e) => setNewTestimonial({...newTestimonial, role: e.target.value})}
                    placeholder="e.g., Wedding Client"
                    className="bg-black/50 border-gold/30 text-white"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-gold mb-2 text-sm">Testimonial Text *</label>
                <Textarea
                  value={newTestimonial.text}
                  onChange={(e) => setNewTestimonial({...newTestimonial, text: e.target.value})}
                  placeholder="What did they say about the show?"
                  className="bg-black/50 border-gold/30 text-white"
                  rows={3}
                />
              </div>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-gold mb-2 text-sm">Image URL</label>
                  <Input
                    value={newTestimonial.image}
                    onChange={(e) => setNewTestimonial({...newTestimonial, image: e.target.value})}
                    placeholder="https://..."
                    className="bg-black/50 border-gold/30 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gold mb-2 text-sm">Rating</label>
                  <Select
                    value={String(newTestimonial.rating)}
                    onValueChange={(value) => setNewTestimonial({...newTestimonial, rating: parseInt(value)})}
                  >
                    <SelectTrigger className="bg-black/50 border-gold/30 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-gold/30">
                      {[5,4,3,2,1].map(r => (
                        <SelectItem key={r} value={String(r)}>{r} Stars</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-white cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newTestimonial.is_logo}
                      onChange={(e) => setNewTestimonial({...newTestimonial, is_logo: e.target.checked})}
                      className="w-5 h-5 accent-gold"
                    />
                    Is Logo (not photo)
                  </label>
                </div>
              </div>
              <Button
                onClick={handleCreateTestimonial}
                disabled={loading}
                className="bg-gold text-black hover:bg-gold/90"
                data-testid="add-testimonial-btn"
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2" size={18} />}
                Add Testimonial
              </Button>
            </div>

            {/* Testimonials List */}
            <div className="bg-black/50 border border-gold/30 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-gold mb-4 flex items-center gap-2">
                <MessageSquare size={24} />
                All Testimonials ({testimonials.length})
              </h2>
              
              {testimonials.length === 0 ? (
                <p className="text-silver text-center py-8">No testimonials yet.</p>
              ) : (
                <div className="space-y-4">
                  {testimonials.map((testimonial) => (
                    <div key={testimonial.id} className="p-4 bg-black/30 border border-gold/20 rounded-xl">
                      {editingTestimonial?.id === testimonial.id ? (
                        <div className="space-y-3">
                          <Input
                            value={editingTestimonial.author}
                            onChange={(e) => setEditingTestimonial({...editingTestimonial, author: e.target.value})}
                            className="bg-black/50 border-gold/30 text-white"
                            placeholder="Author"
                          />
                          <Input
                            value={editingTestimonial.role}
                            onChange={(e) => setEditingTestimonial({...editingTestimonial, role: e.target.value})}
                            className="bg-black/50 border-gold/30 text-white"
                            placeholder="Role"
                          />
                          <Textarea
                            value={editingTestimonial.text}
                            onChange={(e) => setEditingTestimonial({...editingTestimonial, text: e.target.value})}
                            className="bg-black/50 border-gold/30 text-white"
                            rows={3}
                          />
                          <Input
                            value={editingTestimonial.image}
                            onChange={(e) => setEditingTestimonial({...editingTestimonial, image: e.target.value})}
                            className="bg-black/50 border-gold/30 text-white"
                            placeholder="Image URL"
                          />
                          <div className="flex gap-2">
                            <Button onClick={handleUpdateTestimonial} disabled={loading} className="bg-green-600 hover:bg-green-700">
                              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                            </Button>
                            <Button onClick={() => setEditingTestimonial(null)} variant="outline" className="border-gold/30 text-gold">
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-4">
                          {testimonial.image && (
                            <img 
                              src={testimonial.image} 
                              alt={testimonial.author}
                              className={`${testimonial.is_logo ? 'w-16 h-16 object-contain bg-white p-1 rounded' : 'w-16 h-16 rounded-full object-cover'}`}
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-gold font-semibold">{testimonial.author}</span>
                              <div className="flex">
                                {[...Array(testimonial.rating || 5)].map((_, i) => (
                                  <Star key={i} size={12} fill="#D4AF37" stroke="#D4AF37" />
                                ))}
                              </div>
                            </div>
                            {testimonial.role && <p className="text-silver text-sm mb-2">{testimonial.role}</p>}
                            <p className="text-white italic">"{testimonial.text}"</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingTestimonial(testimonial)}
                              className="text-gold hover:bg-gold/10"
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteTestimonial(testimonial.id)}
                              className="text-red-400 hover:bg-red-900/20"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* FAQs Tab */}
        {activeTab === 'faqs' && (
          <div className="space-y-6">
            {/* Seed Button */}
            {faqs.length === 0 && (
              <div className="bg-black/50 border border-gold/30 rounded-2xl p-6 text-center">
                <Database className="h-12 w-12 text-gold mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gold mb-2">No FAQs Found</h3>
                <p className="text-silver mb-4">Seed the database with initial FAQs from the website.</p>
                <Button
                  onClick={handleSeedFaqs}
                  disabled={loading}
                  className="bg-gold text-black hover:bg-gold/90"
                  data-testid="seed-faqs-btn"
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2" size={18} />}
                  Seed FAQs
                </Button>
              </div>
            )}

            {/* Add New FAQ */}
            <div className="bg-black/50 border border-gold/30 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-gold mb-4 flex items-center gap-2">
                <Plus size={24} />
                Add New FAQ
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-gold mb-2 text-sm">Question *</label>
                  <Input
                    value={newFaq.question}
                    onChange={(e) => setNewFaq({...newFaq, question: e.target.value})}
                    placeholder="e.g., What is included in the show fee?"
                    className="bg-black/50 border-gold/30 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gold mb-2 text-sm">Answer *</label>
                  <Textarea
                    value={newFaq.answer}
                    onChange={(e) => setNewFaq({...newFaq, answer: e.target.value})}
                    placeholder="Detailed answer to the question..."
                    className="bg-black/50 border-gold/30 text-white"
                    rows={4}
                  />
                </div>
                <Button
                  onClick={handleCreateFaq}
                  disabled={loading}
                  className="bg-gold text-black hover:bg-gold/90"
                  data-testid="add-faq-btn"
                >
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2" size={18} />}
                  Add FAQ
                </Button>
              </div>
            </div>

            {/* FAQs List */}
            <div className="bg-black/50 border border-gold/30 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-gold mb-4 flex items-center gap-2">
                <HelpCircle size={24} />
                All FAQs ({faqs.length})
              </h2>
              
              {faqs.length === 0 ? (
                <p className="text-silver text-center py-8">No FAQs yet.</p>
              ) : (
                <div className="space-y-4">
                  {faqs.map((faq) => (
                    <div key={faq.id} className="p-4 bg-black/30 border border-gold/20 rounded-xl">
                      {editingFaq?.id === faq.id ? (
                        <div className="space-y-3">
                          <Input
                            value={editingFaq.question}
                            onChange={(e) => setEditingFaq({...editingFaq, question: e.target.value})}
                            className="bg-black/50 border-gold/30 text-white"
                            placeholder="Question"
                          />
                          <Textarea
                            value={editingFaq.answer}
                            onChange={(e) => setEditingFaq({...editingFaq, answer: e.target.value})}
                            className="bg-black/50 border-gold/30 text-white"
                            rows={4}
                          />
                          <div className="flex gap-2">
                            <Button onClick={handleUpdateFaq} disabled={loading} className="bg-green-600 hover:bg-green-700">
                              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                            </Button>
                            <Button onClick={() => setEditingFaq(null)} variant="outline" className="border-gold/30 text-gold">
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <h3 className="text-gold font-semibold mb-2">{faq.question}</h3>
                              <p className="text-silver text-sm">{faq.answer}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingFaq(faq)}
                                className="text-gold hover:bg-gold/10"
                              >
                                <Edit size={16} />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteFaq(faq.id)}
                                className="text-red-400 hover:bg-red-900/20"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
